import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * @file packages/forms/src/SignatureField.tsx
 * @description 表單「手寫簽名」欄位（type: 'signature'）與可複用的簽名採集組件 `SignatureCapture`。
 *
 * 行為與單檔上傳一致：使用者在 Canvas 上手寫簽名 → 轉成 PNG File →
 * 透過 `uploadHandlers.onUploadFile` 上傳，欄位值存上傳後的 fileId（number）。
 * 因此簽名與一般附件共用同一套儲存 / 預覽（`resolveBlobUrl`）/ 移除（`onRemoveFile`）通道。
 *
 * 2026-07 重構（序7 需求）：
 *  - 把「按鈕 + 手寫 Modal + 上傳 + 預覽」抽為 `SignatureCapture`（純以 fileId(number) 值收斂），
 *    供頂層 signature 欄位與「表格內簽名列」（TableField 的 signature column）共用同一套採集邏輯，
 *    避免重複實作與行為漂移。`compact` 模式縮小 UI 供表格格內使用。
 *
 * 為何不復用 web 的 `SignaturePadModal`：
 * - 本組件位於 `@zhcb/forms`，受 monorepo 邊界限制不能 import `apps/web` 的程式碼；
 *   故在本包內以 `react-signature-canvas` 自包含實作（forms 已是前端渲染包，依賴 antd/lucide）。
 *
 * 無 `uploadHandlers` 時（例如純預覽場景）按鈕禁用，與 file/photo 欄位一致。
 */
import { useEffect, useRef, useState } from 'react';
import { Button, Modal, Space, message } from 'antd';
import { Pen } from 'lucide-react';
import SignatureCanvas from 'react-signature-canvas';
/** 簽名 Canvas 高度（px）；寬度跟隨容器自適應。 */
const CANVAS_HEIGHT = 220;
/**
 * 將 dataURL 轉為 PNG File。
 * 不經 fetch，避免受限環境對 data: URI 的 CSP 限制。
 */
function dataUrlToPngFile(dataUrl, filename) {
    const commaIdx = dataUrl.indexOf(',');
    if (commaIdx === -1)
        throw new Error('Invalid data URL: missing comma separator');
    const meta = dataUrl.slice(0, commaIdx);
    const b64 = dataUrl.slice(commaIdx + 1);
    const mime = /data:(.*?);base64/.exec(meta)?.[1] ?? 'image/png';
    const bin = atob(b64);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++)
        bytes[i] = bin.charCodeAt(i);
    return new File([bytes], filename, { type: mime });
}
/**
 * 可複用的手寫簽名採集組件（值收斂為 fileId(number)）。
 * 點「簽名」開啟彈窗作畫 → 完成後上傳並回寫 fileId；已簽名時顯示縮圖預覽。
 * 頂層 `SignatureField` 與 TableField 的 signature 列皆基於此組件實作。
 */
export function SignatureCapture({ fileId, onChange, disabled, uploadHandlers, filePrefix, compact, }) {
    const [open, setOpen] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [isEmpty, setIsEmpty] = useState(true);
    const [canvasWidth, setCanvasWidth] = useState(560);
    const [previewUrl, setPreviewUrl] = useState(null);
    const sigRef = useRef(null);
    const containerRef = useRef(null);
    const canEdit = !disabled && !!uploadHandlers;
    // 已有 fileId 時解析預覽圖（與 file/photo 欄位共用 resolveBlobUrl 通道）
    useEffect(() => {
        if (fileId == null || !uploadHandlers?.resolveBlobUrl) {
            setPreviewUrl(null);
            return;
        }
        let cancelled = false;
        void (async () => {
            const url = await uploadHandlers.resolveBlobUrl?.(fileId);
            if (!cancelled)
                setPreviewUrl(url ?? null);
        })();
        return () => {
            cancelled = true;
        };
    }, [fileId, uploadHandlers]);
    // 彈窗開啟時重置 Canvas 並測量寬度
    useEffect(() => {
        if (!open)
            return;
        setIsEmpty(true);
        const id = window.requestAnimationFrame(() => {
            const w = containerRef.current?.clientWidth ?? 0;
            if (w > 0)
                setCanvasWidth(w);
            sigRef.current?.clear();
        });
        return () => window.cancelAnimationFrame(id);
    }, [open]);
    /** 清除目前簽名軌跡 */
    const handleClear = () => {
        sigRef.current?.clear();
        setIsEmpty(true);
    };
    /** 完成簽字：Canvas → PNG File → 上傳 → 回寫 fileId */
    const handleSubmit = async () => {
        if (!uploadHandlers) {
            message.error('上傳通道未配置');
            return;
        }
        if (!sigRef.current || sigRef.current.isEmpty()) {
            message.warning('請先簽名');
            return;
        }
        try {
            setSubmitting(true);
            const dataUrl = sigRef.current.getCanvas().toDataURL('image/png');
            const file = dataUrlToPngFile(dataUrl, `${filePrefix}-${Date.now()}.png`);
            const uploaded = await uploadHandlers.onUploadFile(file);
            onChange(uploaded.id);
            setOpen(false);
        }
        catch (err) {
            // 上傳失敗：提示並保留彈窗讓使用者重試
            console.error('signature upload failed', err);
            message.error('簽名上傳失敗，請重試');
        }
        finally {
            setSubmitting(false);
        }
    };
    /** 移除已簽名（僅前端移除引用，與 file 欄位一致不調後端軟刪） */
    const handleRemove = () => {
        if (fileId != null)
            uploadHandlers?.onRemoveFile?.(fileId);
        onChange(null);
    };
    // 緊湊模式（表格格內）採用 small 尺寸與較小預覽
    const btnSize = compact ? 'small' : 'middle';
    const previewHeight = compact ? 40 : 80;
    const previewMaxWidth = compact ? 160 : 280;
    return (_jsxs("div", { children: [fileId != null ? (_jsxs(Space, { align: "start", wrap: true, size: compact ? 4 : 8, children: [previewUrl ? (_jsx("img", { src: previewUrl, alt: "\u7C3D\u540D", style: {
                            height: previewHeight,
                            maxWidth: previewMaxWidth,
                            objectFit: 'contain',
                            border: '1px solid #e5e7eb',
                            borderRadius: 6,
                            background: '#fff',
                        } })) : (_jsxs("span", { style: { color: '#6b7280', fontSize: compact ? 12 : undefined }, children: ["\u5DF2\u7C3D\u540D\uFF08#", fileId, "\uFF09"] })), canEdit && (_jsxs(Space, { size: compact ? 4 : 8, children: [_jsx(Button, { size: btnSize, icon: _jsx(Pen, { size: 14 }), onClick: () => setOpen(true), children: compact ? '重簽' : '重新簽名' }), _jsx(Button, { size: btnSize, danger: true, onClick: handleRemove, children: "\u6E05\u9664" })] }))] })) : (_jsx(Button, { size: btnSize, icon: _jsx(Pen, { size: 14 }), disabled: !canEdit, onClick: () => setOpen(true), children: "\u7C3D\u540D" })), _jsxs(Modal, { title: "\u624B\u5BEB\u7C3D\u540D", open: open, onCancel: () => {
                    if (submitting)
                        return;
                    setOpen(false);
                }, footer: null, destroyOnClose: true, width: 640, maskClosable: !submitting, children: [_jsx("div", { style: { fontSize: 13, color: '#6b7280', marginBottom: 8 }, children: "\u8ACB\u5728\u4E0B\u65B9\u6846\u5167\u624B\u5BEB\u7C3D\u540D\uFF08\u53EF\u4F7F\u7528\u6ED1\u9F20\u6216\u89F8\u63A7\uFF09" }), _jsx("div", { ref: containerRef, style: {
                            width: '100%',
                            height: CANVAS_HEIGHT,
                            background: '#fff',
                            border: '1px solid #d1d5db',
                            borderRadius: 6,
                            touchAction: 'none',
                        }, children: _jsx(SignatureCanvas, { ref: (r) => {
                                sigRef.current = r;
                            }, penColor: "#111827", backgroundColor: "#ffffff", canvasProps: {
                                width: canvasWidth,
                                height: CANVAS_HEIGHT,
                                style: { display: 'block', width: '100%', height: '100%' },
                            }, onEnd: () => setIsEmpty(sigRef.current?.isEmpty() ?? true) }) }), _jsxs("div", { style: { marginTop: 16, display: 'flex', justifyContent: 'space-between' }, children: [_jsx(Button, { onClick: handleClear, disabled: submitting || isEmpty, children: "\u6E05\u9664" }), _jsxs(Space, { children: [_jsx(Button, { onClick: () => setOpen(false), disabled: submitting, children: "\u53D6\u6D88" }), _jsx(Button, { type: "primary", onClick: handleSubmit, loading: submitting, disabled: isEmpty, children: "\u5B8C\u6210\u7C3D\u5B57" })] })] })] })] }));
}
/**
 * 手寫簽名欄位（頂層 field type='signature'）。
 * 薄封裝 `SignatureCapture`：把欄位值（fileId(number) 或空）餵入，回寫時包成 number | null。
 */
export function SignatureField({ field, value, onChange, disabled, uploadHandlers, }) {
    const fileId = typeof value === 'number' ? value : null;
    return (_jsx(SignatureCapture, { fileId: fileId, onChange: onChange, disabled: disabled, uploadHandlers: uploadHandlers, filePrefix: `signature-${field.id}` }));
}
//# sourceMappingURL=SignatureField.js.map